// nameHyphenate — wrap a block name into lines that fit inside the
// fixed block-card name field (the field beside the icon). When a
// token is too long to fit on one line, insert a "-" as an explicit
// hyphenation marker and continue on the next line. User-requested:
//
//   > if name of the block is longer than the width of the name field,
//   > add a "-" and line break
//
// The function is intentionally string-in / string-out so the caller
// can render with `white-space: pre-line` and avoid any DOM-measuring
// trickery. Pure — no DOM, no React.
//
// Heuristic:
//   • For each input token, if the running line + token fits within
//     `maxCharsPerLine`, append it.
//   • If a token is longer than `maxCharsPerLine` on its own, slice
//     it in chunks of (maxCharsPerLine - 1) characters and join with
//     "-" + newline so the trailing hyphen survives the line break.
//   • Otherwise (token fits on its own but not on this line), start a
//     new line with the token.

const SAFE_FLOOR_CHARS = 4;

/** Convert a block name into a hyphenated multi-line string that
 *  respects a max-chars-per-line cap. */
export function hyphenateName(name: string, maxCharsPerLine: number): string {
  const cap = Math.max(SAFE_FLOOR_CHARS, Math.floor(maxCharsPerLine));
  const trimmed = name.trim();
  if (!trimmed) return '';
  if (trimmed.length <= cap) return trimmed;

  const tokens = trimmed.split(/\s+/);
  const lines: string[] = [];
  let line = '';

  for (const rawToken of tokens) {
    // Long token: needs explicit "-" hyphenation.
    if (rawToken.length > cap) {
      // First, flush whatever's on the current line.
      if (line) {
        lines.push(line);
        line = '';
      }
      // Chunk the long token. We reserve 1 char per intermediate
      // chunk for the trailing hyphen so the visual width never
      // exceeds `cap`.
      let i = 0;
      while (i < rawToken.length) {
        const remaining = rawToken.length - i;
        if (remaining <= cap) {
          line = rawToken.slice(i);
          break;
        }
        const chunkLen = cap - 1;
        lines.push(rawToken.slice(i, i + chunkLen) + '-');
        i += chunkLen;
      }
      continue;
    }

    // Short token: fit on the current line, else start a new one.
    if (!line) {
      line = rawToken;
    } else if (line.length + 1 + rawToken.length <= cap) {
      line += ' ' + rawToken;
    } else {
      lines.push(line);
      line = rawToken;
    }
  }

  if (line) lines.push(line);
  return lines.join('\n');
}

/** Derive `maxCharsPerLine` from the available pixel width and an
 *  approximate per-character width. Tuned for the block card's `text-sm`
 *  font (≈ 7 px / char). */
export function maxCharsForWidth(availableWidthPx: number, charWidthPx = 7): number {
  return Math.max(SAFE_FLOOR_CHARS, Math.floor(availableWidthPx / charWidthPx));
}
